/**
 * ============================================================================
 * TransactionDetailsModal - Transaction Details View
 * ============================================================================
 *
 * Clean modal design following RefundDetailsModal pattern.
 * Shows detailed information about a ledger transaction.
 *
 * ============================================================================
 */

import { CURRENCY_SYMBOL } from '../../utils/currency';
import React from 'react';
import {
  X,
  DollarSign,
  User,
  Calendar,
  CheckCircle,
  Clock,
  ArrowUpCircle,
  ArrowDownCircle,
  FileText,
  CreditCard,
  Repeat,
  RefreshCw,
  Wallet,
  AlertCircle,
} from 'lucide-react';
import type { Transaction, TransactionType } from '../../types/transactions';
import Modal from '../ui/Modal';

interface TransactionDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: Transaction | null;
}

const TransactionDetailsModal: React.FC<TransactionDetailsModalProps> = ({
  isOpen,
  onClose,
  transaction,
}) => {
  if (!transaction) return null;

  const formatCurrency = (amount: number) => {
    return `${CURRENCY_SYMBOL}${amount.toLocaleString('en-NG', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get transaction type info
  const getTypeConfig = (type: TransactionType) => {
    const configs = {
      booking: {
        label: 'One-time Booking Payment',
        icon: Calendar,
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        textColor: 'text-blue-900',
        iconBg: 'bg-blue-100',
        iconColor: 'text-blue-600',
      },
      recurring: {
        label: 'Recurring Booking Payment',
        icon: Repeat,
        bgColor: 'bg-indigo-50',
        borderColor: 'border-indigo-200',
        textColor: 'text-indigo-900',
        iconBg: 'bg-indigo-100',
        iconColor: 'text-indigo-600',
      },
      membership: {
        label: 'Gym Membership Payment',
        icon: CreditCard,
        bgColor: 'bg-amber-50',
        borderColor: 'border-amber-200',
        textColor: 'text-amber-900',
        iconBg: 'bg-amber-100',
        iconColor: 'text-amber-600',
      },
      refund: {
        label: 'Refund Deduction',
        icon: RefreshCw,
        bgColor: 'bg-purple-50',
        borderColor: 'border-purple-200',
        textColor: 'text-purple-900',
        iconBg: 'bg-purple-100',
        iconColor: 'text-purple-600',
      },
      payout: {
        label: 'Payout to Bank',
        icon: Wallet,
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        textColor: 'text-orange-900',
        iconBg: 'bg-orange-100',
        iconColor: 'text-orange-600',
      },
      debt: {
        label: 'Debt Entry',
        icon: AlertCircle,
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        textColor: 'text-red-900',
        iconBg: 'bg-red-100',
        iconColor: 'text-red-600',
      },
    };
    return configs[type] || configs.booking;
  };

  const typeConfig = getTypeConfig(transaction.type);
  const TypeIcon = typeConfig.icon;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" animate={false}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-xl ${typeConfig.iconBg} flex items-center justify-center`}>
              <TypeIcon className={`w-5 h-5 ${typeConfig.iconColor}`} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Transaction Details</h2>
              <p className="text-sm text-gray-600">{transaction.booking_reference || transaction.transfer_reference || 'N/A'}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-2"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto">
        <div className="p-6 space-y-5">
          {/* Transaction Type Badge */}
          <div className={`${typeConfig.bgColor} ${typeConfig.borderColor} border rounded-xl p-4`}>
            <div className="flex items-center gap-3">
              <TypeIcon className={`w-5 h-5 ${typeConfig.iconColor}`} />
              <div>
                <p className="text-xs text-gray-600">Transaction Type</p>
                <p className={`text-sm font-bold ${typeConfig.textColor}`}>{typeConfig.label}</p>
              </div>
            </div>
          </div>

          {/* Amount Section - Main Focus */}
          <div className={`rounded-xl p-5 ${
            transaction.is_credit
              ? 'bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200'
              : 'bg-gradient-to-br from-red-50 to-rose-50 border border-red-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {transaction.is_credit ? (
                  <ArrowDownCircle className="w-8 h-8 text-green-600" />
                ) : (
                  <ArrowUpCircle className="w-8 h-8 text-red-600" />
                )}
                <div>
                  <p className="text-xs text-gray-600">{transaction.is_credit ? 'Money In (Credit)' : 'Money Out (Debit)'}</p>
                  <p className={`text-2xl font-black ${transaction.is_credit ? 'text-green-600' : 'text-red-600'}`}>
                    {transaction.is_credit ? '+' : '-'}{formatCurrency(transaction.amount)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Balance After */}
          <div className="bg-gray-900 text-white rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <DollarSign className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-300">Balance After This Transaction</span>
              </div>
              {transaction.is_split_payment ? (
                <span className="text-sm font-semibold text-blue-300">Settled by Paystack</span>
              ) : (
                <span className={`text-xl font-bold ${transaction.balance < 0 ? 'text-red-400' : 'text-white'}`}>
                  {formatCurrency(transaction.balance)}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-2">
              {transaction.is_split_payment
                ? 'Paystack settles this venue directly — Spotts does not hold a running balance for these transactions.'
                : 'This balance was recorded at the time of the transaction and is immutable.'}
            </p>
          </div>

          {/* Date & Time */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-xs text-gray-600">Date & Time</p>
                <p className="text-sm font-bold text-gray-900">{formatDate(transaction.date)}</p>
              </div>
            </div>
          </div>

          {/* Customer Info (if available) */}
          {transaction.customer && (
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <User className="w-5 h-5 text-gray-600" />
                <div>
                  <p className="text-xs text-gray-600">Customer</p>
                  <p className="text-sm font-bold text-gray-900">{transaction.customer}</p>
                </div>
              </div>
            </div>
          )}

          {/* Description */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <FileText className="w-5 h-5 text-gray-600 mt-0.5" />
              <div>
                <p className="text-xs text-gray-600">Description</p>
                <p className="text-sm font-semibold text-gray-900">{transaction.description}</p>
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="bg-gray-50 rounded-xl p-4">
            <div className="flex items-center gap-3">
              {transaction.status === 'completed' || transaction.status === 'processed' || transaction.status === 'cleared' ? (
                <CheckCircle className="w-5 h-5 text-green-600" />
              ) : transaction.status === 'pending' || transaction.status === 'processing' ? (
                <Clock className="w-5 h-5 text-orange-600" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-600" />
              )}
              <div>
                <p className="text-xs text-gray-600">Status</p>
                <p className="text-sm font-bold text-gray-900 capitalize">{transaction.status}</p>
              </div>
            </div>
          </div>

          {/* Booking Reference (if different from header) */}
          {transaction.booking_reference && (
            <div className="bg-gray-50 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <CreditCard className="w-5 h-5 text-gray-600" />
                <div>
                  <p className="text-xs text-gray-600">Reference</p>
                  <p className="text-sm font-bold text-gray-900 font-mono">{transaction.booking_reference}</p>
                </div>
              </div>
            </div>
          )}
        </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
          <button
            onClick={onClose}
            className="w-full bg-gray-900 text-white py-3 rounded-xl font-semibold hover:bg-gray-800 transition-colors"
          >
            Close
          </button>
        </div>
    </Modal>
  );
};

export default TransactionDetailsModal;
